import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CouponCurrency } from '@/@types/Coupons';
import { Label } from '@/components/shared/Label/Label';
import { cn } from '@/lib/utils';
import { MoneyInput } from './MoneyInput';
import {
  COUPON_CURRENCIES,
  convertCouponAmount,
  otherCouponCurrency,
} from './couponScope';

const rateFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface CouponAmountFieldProps {
  label?: string;
  /** Usado quando o campo não tem label visível. */
  ariaLabel?: string;
  /** Valor em centavos, na moeda `currency`. */
  value: number | null;
  onChange: (valueInCents: number) => void;
  /** Moeda-base: a que o vendedor digita e a que vai para a API. */
  currency: CouponCurrency;
  /** Moedas das ofertas marcadas. Com duas, entra a conversão. */
  scopeCurrencies: CouponCurrency[];
  /**
   * Troca de moeda-base pelos cards. Sem ele, a versão é a compacta: sem aviso,
   * sem cards e sem taxa — já explicados no campo de desconto.
   */
  onCurrencyChange?: (currency: CouponCurrency) => void;
  /** Cotação "1 USD = `usdToBrl` BRL"; ausente enquanto carrega ou se falhou. */
  usdToBrl?: number;
  isRateLoading?: boolean;
  subLabel?: string;
  helperText?: string;
  disabled?: boolean;
  dataCy?: string;
}

/**
 * Valor em dinheiro do cupom. Ofertas numa moeda só: campo simples nessa
 * moeda. Ofertas em BRL e USD: o vendedor digita numa e vê o equivalente na
 * outra, calculado na cotação atual — o cupom guarda só a moeda-base.
 */
export function CouponAmountField({
  label,
  ariaLabel,
  value,
  onChange,
  currency,
  scopeCurrencies,
  onCurrencyChange,
  usdToBrl,
  isRateLoading,
  subLabel,
  helperText,
  disabled,
  dataCy,
}: CouponAmountFieldProps) {
  const { t } = useTranslation('common');
  const isCompact = !onCurrencyChange;

  if (scopeCurrencies.length < 2) {
    return (
      <div className='flex flex-col gap-2.5'>
        {label && <Label className='!text-white'>{label}</Label>}
        {scopeCurrencies.length === 1 && (
          <p className='text-sm text-zinc-500'>
            {t('createCoupon.scopeCurrencyHint', { currency })}
          </p>
        )}
        <MoneyInput
          ariaLabel={ariaLabel ?? label}
          value={value}
          onChange={onChange}
          currency={currency}
          subLabel={subLabel}
          helperText={helperText}
          disabled={disabled}
          dataCy={dataCy}
        />
      </div>
    );
  }

  const otherCurrency = otherCouponCurrency(currency);
  const equivalent =
    usdToBrl === undefined
      ? null
      : convertCouponAmount(value ?? 0, currency, usdToBrl);

  return (
    <div className='flex flex-col gap-2.5'>
      {label && <Label className='!text-white'>{label}</Label>}

      {!isCompact && (
        <>
          <div className='flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-3'>
            <span className='mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary' />
            <p className='text-sm leading-relaxed text-primary/90'>
              {t('createCoupon.multiCurrencyNotice')}
            </p>
          </div>

          <p className='text-sm text-zinc-500'>
            {t('createCoupon.currencyQuestion')}
          </p>

          <div className='grid grid-cols-2 gap-2.5' role='radiogroup'>
            {COUPON_CURRENCIES.map((option) => {
              const isSelected = option === currency;

              return (
                <button
                  key={option}
                  type='button'
                  role='radio'
                  aria-checked={isSelected}
                  disabled={disabled}
                  onClick={() => !isSelected && onCurrencyChange?.(option)}
                  data-cy={`card-coupon-currency-${option}`}
                  className={cn(
                    'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    isSelected
                      ? 'border-primary bg-primary/10'
                      : 'border-colorBorder hover:border-zinc-600'
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                      isSelected ? 'border-primary' : 'border-zinc-500'
                    )}
                  >
                    {isSelected && (
                      <span className='h-2 w-2 rounded-full bg-primary' />
                    )}
                  </span>
                  <span className='flex flex-col gap-0.5'>
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        isSelected ? 'text-primary' : 'text-zinc-200'
                      )}
                    >
                      {t('createCoupon.defineIn', { currency: option })}
                    </span>
                    <span className='text-xs leading-snug text-zinc-500'>
                      {t('createCoupon.defineInHint', {
                        currency: otherCouponCurrency(option),
                      })}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <p className='text-sm text-zinc-500'>
        {t('createCoupon.valueIn', { currency })}
      </p>
      <MoneyInput
        ariaLabel={t('createCoupon.valueIn', { currency })}
        value={value}
        onChange={onChange}
        currency={currency}
        subLabel={subLabel}
        helperText={helperText}
        disabled={disabled}
        dataCy={dataCy}
      />

      <p className='text-sm text-zinc-500'>
        {t('createCoupon.equivalentIn', { currency: otherCurrency })}
      </p>
      <MoneyInput
        ariaLabel={t('createCoupon.equivalentIn', { currency: otherCurrency })}
        value={equivalent}
        currency={otherCurrency}
        readOnly
        dataCy={dataCy && `${dataCy}-equivalent`}
      />

      {!isCompact && (
        <div className='flex items-center justify-between gap-3 rounded-md border border-colorBorder bg-[#09090B] px-3 py-2.5'>
          <span className='flex items-center gap-2 text-sm text-zinc-400'>
            <Info className='h-4 w-4 shrink-0' />
            {t('createCoupon.exchangeRate')}
          </span>
          <span className='whitespace-nowrap text-sm font-bold text-primary'>
            {usdToBrl !== undefined
              ? `1 USD = ${rateFormatter.format(usdToBrl)} BRL`
              : isRateLoading
                ? '...'
                : t('createCoupon.exchangeRateError')}
          </span>
        </div>
      )}
    </div>
  );
}
